/**
 * rc111 — Capturador de etiqueta de la ONU (portado del LabelCapture de ByteACS):
 * escanea los códigos de barras del SERIAL y la MAC con la cámara (o una foto
 * de galería) usando @zxing. Todo es confirmable/editable antes de usar — el
 * barcode es exacto, el operador valida.
 *
 * Clasificación por FORMA (no por longitud):
 *   Serial = EXACTAMENTE 16 hex (SN Huawei, forma larga de la etiqueta)
 *   MAC    = 12 hex al inicio, opcional separador + basura ('…D733-43(17)')
 *   PROD ID u otro (trae letras no-hex) → se ignora.
 *
 * Tip operativo (de ByteACS): mejor desde el CELULAR — la webcam de laptop no
 * enfoca barcodes chiquitos; ahí conviene "Subir foto" nítida y de cerca.
 */
import { BrowserMultiFormatReader, type IScannerControls } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { Camera, Check, ImageUp, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export type EtiquetaOnu = { serial?: string; mac?: string };

function makeReader(): BrowserMultiFormatReader {
  const hints = new Map();
  hints.set(DecodeHintType.TRY_HARDER, true);
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.ITF,
  ]);
  return new BrowserMultiFormatReader(hints);
}

function classify(raw: string): EtiquetaOnu {
  const t = (raw || "").trim().toUpperCase();
  if (/^[0-9A-F]{16}$/.test(t)) return { serial: t };
  const m = t.match(/^([0-9A-F]{12})(?:[-\s(].*)?$/);
  if (m) return { mac: m[1] };
  return {};
}

export function fmtMac(hex: string): string {
  const h = (hex || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase().slice(0, 12);
  return h.match(/.{1,2}/g)?.join(":") ?? h;
}

export function LabelCaptureOnu({
  onClose, onUse,
}: {
  onClose: () => void;
  onUse: (d: EtiquetaOnu) => void;
}) {
  const [serial, setSerial] = useState("");
  const [mac, setMac] = useState("");
  const [photo, setPhoto] = useState("");
  const [cam, setCam] = useState(false);
  const [err, setErr] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);

  function take(res: EtiquetaOnu) {
    if (res.serial) setSerial(res.serial);
    if (res.mac) setMac(res.mac);
  }

  // Cámara en vivo — trasera, alta resolución y autofocus continuo (celular)
  useEffect(() => {
    if (!cam) return;
    let alive = true;
    const reader = makeReader();
    const constraints: MediaStreamConstraints = {
      video: {
        facingMode: { ideal: "environment" },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        advanced: [{ focusMode: "continuous" }] as unknown as MediaTrackConstraints[],
      },
    };
    reader
      .decodeFromConstraints(constraints, videoRef.current!, (result) => {
        if (result && alive) take(classify(result.getText()));
      })
      .then((c) => { if (alive) controlsRef.current = c; else c.stop(); })
      .catch(() => {
        if (alive) setErr('No pude abrir la cámara. Usa "Subir foto" o teclea a mano.');
      });
    return () => {
      alive = false;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
  }, [cam]);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    setPhoto(url);
    setErr("");
    const reader = makeReader();
    try {
      take(classify((await reader.decodeFromImageUrl(url)).getText()));
      return;
    } catch { /* reintento a mayor resolución */ }
    try {
      const img = await new Promise<HTMLImageElement>((res, rej) => {
        const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = url;
      });
      const scale = Math.min(3, Math.max(1, 2000 / (img.naturalWidth || 1000)));
      const cv = document.createElement("canvas");
      cv.width = Math.round((img.naturalWidth || 1000) * scale);
      cv.height = Math.round((img.naturalHeight || 1000) * scale);
      cv.getContext("2d")!.drawImage(img, 0, 0, cv.width, cv.height);
      take(classify((await reader.decodeFromCanvas(cv)).getText()));
    } catch {
      setErr(
        "No detecté un código de barras en la foto (borrosa o lejos). " +
        "Acércate al barcode y que salga enfocado, o teclea los datos viendo la foto.",
      );
    }
  }

  const inputCls =
    "w-full rounded-md border bg-background px-3 py-2 text-sm outline-none focus:border-primary/60";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[440px] rounded-xl border bg-card p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-semibold">Capturar de la etiqueta</div>
          <button
            type="button" onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={16} />
          </button>
        </div>
        <p className="mb-3 text-[11px] text-muted-foreground">
          Escanea los códigos de barras del serial y la MAC (exacto). Mejor desde
          el <strong>celular</strong> (autofocus) — en laptop usa &quot;Subir
          foto&quot; nítida y de cerca.
        </p>

        <div className="mb-3 flex gap-2">
          <button
            type="button"
            onClick={() => setCam((v) => !v)}
            className={
              "flex flex-1 items-center justify-center gap-1.5 rounded-md border py-2 font-mono text-[11px] transition " +
              (cam
                ? "border-primary bg-primary/10 text-primary"
                : "border-border bg-muted/30 text-muted-foreground hover:text-foreground")
            }
          >
            <Camera size={13} /> {cam ? "Cámara activa" : "Usar cámara"}
          </button>
          <label className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border bg-muted/30 py-2 font-mono text-[11px] text-muted-foreground transition hover:text-foreground">
            <ImageUp size={13} /> Subir foto
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />
          </label>
        </div>

        {cam && (
          <div className="mb-3 overflow-hidden rounded-lg border bg-black">
            <video ref={videoRef} className="block max-h-[220px] w-full object-cover" muted playsInline />
          </div>
        )}
        {photo && !cam && (
          <div className="mb-3 overflow-hidden rounded-lg border bg-black">
            <img src={photo} alt="etiqueta" className="block max-h-[220px] w-full object-contain" />
          </div>
        )}
        {err && (
          <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-600 dark:text-amber-400">
            {err}
          </div>
        )}

        <div className="space-y-2.5">
          <div>
            <label className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              Serial {serial && <Check size={11} className="text-emerald-500" />}
            </label>
            <input
              value={serial}
              onChange={(e) => setSerial(e.target.value.toUpperCase())}
              placeholder="48575443E677FEB7"
              className={`${inputCls} font-mono`}
            />
          </div>
          <div>
            <label className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              MAC {mac && <Check size={11} className="text-emerald-500" />}
            </label>
            <input
              value={mac}
              onChange={(e) => setMac(e.target.value.toUpperCase())}
              placeholder="14656A95ACF6"
              className={`${inputCls} font-mono`}
            />
            {mac && (
              <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                {fmtMac(mac)}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button" onClick={onClose}
            className="flex-1 rounded-md border py-2 text-xs font-semibold text-muted-foreground"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => {
              onUse({ serial: serial || undefined, mac: mac || undefined });
              onClose();
            }}
            disabled={!serial && !mac}
            className="flex-1 rounded-md bg-primary py-2 text-xs font-bold text-primary-foreground disabled:opacity-40"
          >
            Usar estos datos
          </button>
        </div>
      </div>
    </div>
  );
}
